import { light } from "@fortawesome/fontawesome-svg-core/import.macro";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import NoLOIcon from "assets/images/v2/loan-officer-silhouette.svg";
import { useState } from "react";
import { LoanOfficerDetail } from "pages/application/applicationService";
import { formatPhoneNumber } from "utilities/string";
import "./loan-officer-card-v2.scss";
import { GLOBAL_LINK_NO_TX_CLASS } from "utilities/globalLinkNoTx";
import { getLoDisplayFields, isCallQueueOrEmptyLoanOfficer } from "utilities/loanOfficerDisplay";
import { LoanOfficerCardV2Skeleton } from "./LoanOfficerCardSkeleton";

interface LoanOfficerCardV2Props {
  readonly loInfo?: LoanOfficerDetail;
  readonly isLoading?: boolean;
  readonly onPhoneClick?: () => void;
  readonly onEmailClick?: () => void;
  readonly onHeadshotError?: () => void;
}

function LoanOfficerCardV2({ loInfo, isLoading, onPhoneClick, onEmailClick, onHeadshotError }: LoanOfficerCardV2Props) {
  const [imageLoadError, setImageLoadError] = useState(false);

  if (isLoading && !loInfo) {
    return <LoanOfficerCardV2Skeleton />;
  }

  if (!loInfo || isCallQueueOrEmptyLoanOfficer(loInfo)) {
    return null;
  }

  const { title, email, phone } = getLoDisplayFields(loInfo);
  const phoneNumber = phone || loInfo.phoneNumber;

  return (
    <div className="lo-card-v2">
      <p className="lo-card-v2__title">Your loan officer is ready to help</p>
      <p className="lo-card-v2__subtitle">You&#39;ve been invited to start your application</p>
      <hr className="lo-card-v2__divider" />
      <div className="lo-card-v2__info">
        <div className="lo-card-v2__avatar">
          {loInfo.photoImageUrl && !imageLoadError ? (
            <img
              src={loInfo.photoImageUrl}
              alt={loInfo.fullName}
              onError={() => {
                setImageLoadError(true);
                onHeadshotError?.();
              }}
            />
          ) : (
            <img src={NoLOIcon} alt="No loan officer" />
          )}
        </div>
        <span className={`lo-card-v2__name ${GLOBAL_LINK_NO_TX_CLASS}`} data-testid="lo-officer-name">{loInfo.fullName}</span>
        {title && <span className="lo-card-v2__job-title">{title}</span>}
        {loInfo.nmlsLicenseNumber && (
          <span className="lo-card-v2__nmls">{`NMLS #${loInfo.nmlsLicenseNumber}`}</span>
        )}
      </div>
      <div className="lo-card-v2__contact">
        <span className="lo-card-v2__contact-label">CONTACT</span>
        {email && (
          <a 
            href={`mailto:${email}`} 
            onClick={onEmailClick}
            className="lo-card-v2__contact-row lo-card-v2__contact-email"
          >
            <span className="lo-card-v2__contact-icon">
              <FontAwesomeIcon icon={light("envelope-open")} />
            </span>
            <span className={GLOBAL_LINK_NO_TX_CLASS} data-testid="lo-officer-email">{email}</span>
          </a>
        )}
        {phoneNumber && (
          <a 
            href={`tel:${phoneNumber}`}
            onClick={onPhoneClick}
            className="lo-card-v2__contact-row"
          >
            <span className="lo-card-v2__contact-icon">
              <FontAwesomeIcon icon={light("phone")} />
            </span>
            <span className={GLOBAL_LINK_NO_TX_CLASS} data-testid="lo-officer-phone">{formatPhoneNumber(phoneNumber)}</span>
          </a>
        )}
      </div>
    </div>
  );
}

export default LoanOfficerCardV2;
